import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import InvoiceDetailView from './InvoiceDetailView';
import { 
  Search, 
  Download, 
  Eye, 
  CheckCircle, 
  Clock, 
  XCircle, 
  FileText, 
  Printer,
  Calendar,
  DollarSign,
  TrendingUp,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const monthlyInvoices = {
  '2025-10': [
    {
      number: 'INV-2024-1247',
      date: '2025-10-24',
      dueDate: '2025-11-24',
      client: 'Acme Corporation',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Professional Services', quantity: 1, unitPrice: 14514.29, taxRate: 5, amount: 15240 }
      ],
      subtotal: 14514.29,
      tax: 725.71,
      total: 15240,
      status: 'processed',
      ftaRef: 'FTA-2024-XYZ1247',
      erpSource: 'SAP',
      paymentStatus: 'Paid'
    },
    {
      number: 'INV-2024-1246',
      date: '2025-10-23',
      dueDate: '2025-11-23',
      client: 'Tech Ventures LLC',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Software License', quantity: 5, unitPrice: 4285.71, taxRate: 5, amount: 22500 }
      ],
      subtotal: 21428.57,
      tax: 1071.43,
      total: 22500,
      status: 'processed',
      ftaRef: 'FTA-2024-XYZ1246',
      erpSource: 'SAP',
      paymentStatus: 'Paid'
    },
    {
      number: 'INV-2024-1245',
      date: '2025-10-23',
      dueDate: '2025-11-23',
      client: 'Global Trading FZ',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Consulting Services', quantity: 10, unitPrice: 833.33, taxRate: 5, amount: 8750 }
      ],
      subtotal: 8333.33,
      tax: 416.67,
      total: 8750,
      status: 'pending',
      ftaRef: 'Pending',
      erpSource: 'SAP',
      paymentStatus: 'Unpaid'
    }
  ],
  '2025-09': [
    {
      number: 'INV-2024-1185',
      date: '2025-09-28',
      dueDate: '2025-10-28',
      client: 'Smart Solutions',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'IT Support Package', quantity: 1, unitPrice: 11714.29, taxRate: 5, amount: 12300 }
      ],
      subtotal: 11714.29,
      tax: 585.71,
      total: 12300,
      status: 'processed',
      ftaRef: 'FTA-2024-XYZ1185',
      erpSource: 'SAP',
      paymentStatus: 'Paid'
    }
  ]
};

export default function SalesInvoices() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-10');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isDetailView, setIsDetailView] = useState(false);

  const currentInvoices = monthlyInvoices[selectedMonth as keyof typeof monthlyInvoices] || [];

  const filteredInvoices = currentInvoices.filter(invoice =>
    invoice.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.client.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'processed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Processed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setIsDetailView(true);
  };

  const handleBackToList = () => {
    setIsDetailView(false);
    setSelectedInvoice(null);
  };

  const handleDownloadPDF = () => {
    toast.success('PDF download started');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    toast.success('Sales invoices exported successfully');
  };

  const formatMonthDisplay = (month: string) => {
    const date = new Date(month + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Show detail view if an invoice is selected
  if (isDetailView && selectedInvoice) {
    return (
      <InvoiceDetailView 
        invoice={selectedInvoice} 
        onBack={handleBackToList}
        type="sales"
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#0052CC] rounded-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl text-gray-900">Sales Invoices</h1>
            <p className="text-gray-600 mt-1">View and manage your sales invoices by month</p>
          </div>
        </div>
        <Button className="bg-[#0052CC] hover:bg-[#0041A3]" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Invoices</p>
                <p className="text-3xl text-gray-900">{currentInvoices.length}</p>
                <p className="text-xs text-gray-500 mt-2">{formatMonthDisplay(selectedMonth)}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                <p className="text-3xl text-gray-900">
                  {currentInvoices.reduce((sum, inv) => sum + inv.total, 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-2">AED</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Processed</p>
                <p className="text-3xl text-green-600">
                  {currentInvoices.filter(inv => inv.status === 'processed').length}
                </p>
                <p className="text-xs text-green-600 mt-2">Successfully sent to FTA</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Tax (VAT)</p>
                <p className="text-3xl text-purple-600">
                  {currentInvoices.reduce((sum, inv) => sum + inv.tax, 0).toLocaleString()}
                </p>
                <p className="text-xs text-purple-600 mt-2">AED</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by invoice number or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="flex-1 text-left">{formatMonthDisplay(selectedMonth)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025-10">October 2025</SelectItem>
                  <SelectItem value="2025-09">September 2025</SelectItem>
                  <SelectItem value="2025-08">August 2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0052CC]" />
                Sales Invoices - {formatMonthDisplay(selectedMonth)}
              </CardTitle>
              <CardDescription className="mt-1">
                Showing {filteredInvoices.length} of {currentInvoices.length} invoices
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Service Provider</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead className="text-right">Subtotal (AED)</TableHead>
                  <TableHead className="text-right">Tax (AED)</TableHead>
                  <TableHead className="text-right">Total (AED)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <FileText className="w-12 h-12 text-gray-300" />
                        <p>No invoices found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.number} className="hover:bg-gray-50/50">
                      <TableCell className="text-[#0052CC]">{invoice.number}</TableCell>
                      <TableCell className="text-gray-700">{invoice.date}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{invoice.client}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                          {invoice.serviceProvider}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{invoice.items.length} item(s)</Badge>
                      </TableCell>
                      <TableCell className="text-right text-gray-700">{invoice.subtotal.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-gray-700">{invoice.tax.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-gray-900">{invoice.total.toLocaleString()}</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        <Badge className={invoice.paymentStatus === 'Paid' ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100'}>
                          {invoice.paymentStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-[#0052CC] hover:text-[#0041A3] hover:bg-blue-50 h-8 w-8 p-0"
                          onClick={() => handleViewInvoice(invoice)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
